import get from 'lodash.get';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import {
    compareResultByStudentMeasureYearGradePeriod,
    getR3DResult,
    getParsedTrcStatus,
    getStatuses,
    updateStudentObject as sharedUpdateStudentObject,
    isImportedResult,
    formatObservations,
    getStudentResults,
    getCompositeStatus,
    buildStatus,
} from '../../../utils/shared/resultReducerHelper';
import { GATING, KEY, PERIOD, PROBE_DETAIL } from '../../../constants/shared/CommonConstants';
import { MEASURE } from '../../../constants/shared/MeasuresConstants';
import { MEASURE as READING_MEASURES } from '../../../constants/reading/MeasuresConstants';
import {
    getTroResult,
    reformatMeasureScores,
    hasUntranscribableResult,
    getOnGradeBookVrr,
} from '../../../utils/reading/resultReducerHelper';
import {
    getTrcInterpretation,
    PROFICIENT_LEVEL,
} from '../../../constants/shared/TrcInterpretationMapper';
import { lexileGradeAvailability } from '../../../constants/reading/LexileGradeMapper';
import { resultStateProperties } from '../../../constants/reading/CommonConstants';
import {
    COMMON_PROFICIENT_MAPPER,
    COMMON_PROFICIENT_SCORE_MAPPER,
    getCommonProficientLevel,
    PROFICIENT_LEVEL_MAPPER,
} from '../../../constants/reading/ProficientLevelMapper';
import sortSubmeasuresByPeriodAndGrade from '../../../features/probeDetails/reading/do/DecodingProbeDetailsMapper';
import { getCommonInterpretation } from '../../../constants/shared/CommonInterpretationMapper';
import { parseEvents, parseGrowthZones } from '../shared/resultReducerParsers';
import { convertMsToSeconds, isSecure } from '../../../utils/shared/commonUtils';
import { EVENT_TYPES } from '../../../constants/shared/ProbeDetailsConstants';
import { TEXT_LEVELS_MAPPER } from '../../../constants/reading/TroConstants';

const { PM, SUMMER } = PERIOD;
const { OVERALL_SCORE, CLIENT_MOD_DATE, MEASURE_RESULT_INTERP, INTERPRETATION } = KEY;
const { TRC, MAZE } = MEASURE;
const { LEXILE, DO, TRO, RAN } = READING_MEASURES;
const { DISCONTINUE } = GATING;
const { WELL_BELOW_PROFICIENT } = PROFICIENT_LEVEL;
const { CORRECT_RESPONSE, NOT_CORRECT_RESPONSE } = PROBE_DETAIL;

export const updateStudentObject = (students, studentId, results, statuses, growthZones = []) =>
    sharedUpdateStudentObject(
        students,
        studentId,
        { results, statuses, growthZones },
        resultStateProperties,
    );

export const parseBurstStatus = burstResult => ({
    schoolYear: burstResult.schoolYear,
    period: burstResult.period,
    grade: burstResult.grade,
    formGrade: burstResult.grade,
    measure: burstResult.genus,
    score: burstResult.overallScore,
    isBenchmark: burstResult.benchmark,
    isUnofficial: burstResult.hasUnofficialScore,
    resultId: burstResult.resultGroupSid,
    date: burstResult.stringDateCompleted,
    isComplete: get(burstResult, 'result', { complete: false }).complete,
    probeMaxScore: burstResult.probeMaxScore,
    formNumber: burstResult.form,
});

export const parseBurstResult = (burstResult, studentId) => ({
    measure: burstResult.genus,
    schoolYear: burstResult.schoolYear,
    period: burstResult.period,
    grade: burstResult.grade,
    formGrade: burstResult.grade,
    studentId,
    score: burstResult.overallScore,
    resultId: burstResult.resultGroupSid,
    isBenchmark: burstResult.benchmark,
    date: burstResult.stringDateCompleted,
    isComplete: get(burstResult, 'result', { complete: false }).complete,
    probeMaxScore: burstResult.probeMaxScore,
    formNumber: burstResult.form,
});

const parseDecodeStatus = burstResult => ({
    schoolYear: burstResult.school_year,
    period: burstResult.period,
    grade: burstResult.grade_level,
    formGrade: burstResult.grade_level,
    measure: burstResult.measure,
    score: burstResult.score,
    isUnofficial: burstResult.officialness_rank !== 1,
    resultId: burstResult.result_id,
    date: burstResult.client_create_date,
    isComplete: burstResult.is_valid && burstResult.officialness_rank === 1,
    probeMaxScore: burstResult.max_score,
    formNumber: burstResult.form_number,
});

const parseDecodeResult = (burstResult, studentId) => ({
    measure: burstResult.measure,
    schoolYear: burstResult.school_year,
    period: burstResult.period,
    grade: burstResult.grade_level,
    formGrade: burstResult.grade_level,
    studentId,
    score: burstResult.score,
    resultId: burstResult.result_id,
    date: burstResult.client_create_date,
    isComplete: burstResult.is_valid && burstResult.officialness_rank === 1,
    probeMaxScore: burstResult.max_score,
    formNumber: burstResult.form_number,
    formUid: burstResult.form_uid,
    itemLevelDetails: burstResult.item_level_details,
    assessor: burstResult.staff_uid,
});

export const parseDecodeResultsData = decodeResults => {
    let students = {};
    if (isEmpty(decodeResults) || isEmpty(decodeResults.results)) return students;
    const uniqueStudentsId = [...new Set(decodeResults.results.map(result => result.student_uid))];
    uniqueStudentsId.forEach(studentId => {
        const burstResults = decodeResults.results.filter(
            result => result.student_uid === studentId,
        );
        const results = burstResults.map(burstResult => parseDecodeResult(burstResult, studentId));
        const statuses = burstResults.map(burstResult => parseDecodeStatus(burstResult));
        students = updateStudentObject(students, studentId, results, statuses);
    });
    return students;
};

export const parseR3DData = r3dResponse => {
    let students = {};
    if (isEmpty(r3dResponse)) return students;

    const trcResponse = get(r3dResponse, TRC, {});
    Object.keys(get(trcResponse, 'studentsScores', {})).forEach(studentId => {
        const trcResults = trcResponse.studentsScores[studentId];
        const results = trcResults.map(trcResult => getR3DResult(trcResult, TRC, studentId));
        const statuses = trcResults
            .map(trcResult => getParsedTrcStatus(trcResult))
            .filter(
                trcResult =>
                    trcResult.period !== PM || (trcResult.isComplete && trcResult.period === PM),
            );
        students = updateStudentObject(students, studentId, results, statuses);
    });

    const burstResponse = get(r3dResponse, 'JOULE', {});
    Object.keys(get(burstResponse, 'resultGroups', {})).forEach(studentId => {
        const burstResults = burstResponse.resultGroups[studentId];
        const results = burstResults.map(burstResult => parseBurstResult(burstResult, studentId));
        const statuses = burstResults.map(burstResult => parseBurstStatus(burstResult));
        students = updateStudentObject(students, studentId, results, statuses);
    });
    return students;
};

export const parseTroResult = (result, measureResult) => ({
    measure: TRO,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    formGrade: result.grade_level,
    measureResult,
    studentId: result.student_uid,
    resultId: result.result_id,
    interp: getTrcInterpretation(get(result, MEASURE_RESULT_INTERP)),
    modifiedDate: result.mod_date,
    isComplete: result.is_complete,
    isUnofficial: result.hasUnofficialScore,
    isUntranscribable: hasUntranscribableResult(measureResult.bookResults),
    isSecure: isSecure(result),
});

export const parseTroStatus = (result, measureResult) => {
    const { isDiscontinued, bookResults } = measureResult;
    return {
        measure: TRO,
        measureResult,
        schoolYear: result.school_year,
        period: result.period,
        grade: result.grade_level,
        resultId: result.result_id,
        proficientLevel: getCommonProficientLevel(
            get(result, MEASURE_RESULT_INTERP),
            COMMON_PROFICIENT_MAPPER,
        ),
        score: getCommonProficientLevel(
            isDiscontinued ? WELL_BELOW_PROFICIENT : get(result, MEASURE_RESULT_INTERP),
            COMMON_PROFICIENT_SCORE_MAPPER,
        ),
        interp: getTrcInterpretation(
            isDiscontinued ? WELL_BELOW_PROFICIENT : get(result, MEASURE_RESULT_INTERP),
        ),
        isComplete: result.is_complete,
        date: result.mod_date,
        isUntranscribable: hasUntranscribableResult(bookResults),
        onGradeBookVrr: result.is_complete ? getOnGradeBookVrr(bookResults) : {},
        gatingType: isDiscontinued ? DISCONTINUE : null,
        isSecure: isSecure(result),
    };
};

export const parseTroData = troResponse => {
    let students = {};
    if (isEmpty(troResponse)) return students;
    troResponse.forEach(troResult => {
        const { result, status } = getTroResult(troResult);
        students = updateStudentObject(students, troResult.student_uid, [result], [status]);
    });
    return students;
};

const parseTroVrrResult = ({
    score,
    accuracy,
    error_reason: errorReason,
    start_position: startPosition,
    end_position: endPosition,
    item_level_details: itemLevelDetails,
    audio_duration: audioDuration,
}) => {
    const events = get(itemLevelDetails, 'events', []);
    const parsedEvents = parseEvents(events);

    return {
        score,
        accuracy,
        startPosition,
        endPosition,
        audioDuration: convertMsToSeconds(audioDuration),
        itemLevelDetails: parsedEvents,
        isUntranscribable: !isNil(errorReason) && isNil(score),
        isRescored:
            parsedEvents.some(event => event.eventDetails.source === EVENT_TYPES.USER_EVENT) ||
            (!isNil(errorReason) && !isNil(endPosition)),
    };
};

const parseTroComprehensionResult = ({
    max_score: maxScore,
    score,
    item_level_details: itemLevelDetails,
}) => {
    const correctnessValue = 1;

    return {
        maxScore,
        score,
        itemLevelDetails: get(itemLevelDetails, 'items', []).map(
            ({ score: itemScore, item_idx: itemIdx, item_type: itemType, response }) => ({
                score: itemScore === correctnessValue ? CORRECT_RESPONSE : NOT_CORRECT_RESPONSE,
                itemIdx: itemIdx.toString(),
                itemType,
                response,
            }),
        ),
    };
};

export const parseTroMeasureResult = ({
    book_results: bookResults,
    interpretation,
    reading_level: readingLevel,
    is_discontinued: isDiscontinued,
}) => ({
    bookResults: bookResults.map(result => ({
        bookGenre: result.book_genre,
        bookId: result.book_id,
        isValid: result.is_valid,
        canEdit: result.can_edit,
        bookReadingLevel: result.book_reading_level,
        bookTitle: result.book_title,
        passingLevel: result.passing_level,
        comprehensionResult: parseTroComprehensionResult(result.comprehension_result),
        relativeLevel: TEXT_LEVELS_MAPPER[result.relative_level],
        vrrResult: parseTroVrrResult(result.vrr_result),
    })),
    isDiscontinued,
    readingLevel,
    interpretation,
});

const parseRanStatus = (status, result) => ({
    measure: RAN,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    resultId: result.result_id,
    score: get(status, OVERALL_SCORE),
    interp: getCommonInterpretation(get(status, INTERPRETATION)),
    isComplete: true,
    date: result.mod_date,
    isSecure: isSecure(result),
});

const parseRanMeasureResult = measureResult => ({
    responses: measureResult.responses.map(item => ({
        id: item.item_id,
        outcome: item.outcome,
    })),
});

const parseRanResult = (status, result) => ({
    measure: RAN,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    formGrade: result.grade_level,
    studentId: result.student_uid,
    resultId: result.result_id,
    formUid: result.form_id,
    measureResult: parseRanMeasureResult(result.measure_result),
    measureScores: {
        [RAN]: {
            score: get(status, OVERALL_SCORE),
            interp: getCommonInterpretation(get(status, INTERPRETATION)),
            maxScore: 0,
            correctResponses: result.measure_result.correct_responses,
        },
    },
    date: result.mod_date,
    isSecure: isSecure(result),
});

export const parseRanData = ranResponse => {
    let students = {};

    if (isEmpty(ranResponse)) return students;
    ranResponse.results.forEach(result => {
        const studentStatus = ranResponse.student_statuses.find(status =>
            compareResultByStudentMeasureYearGradePeriod(
                status,
                result.student_uid,
                null,
                result.school_year,
                result.grade_level,
                result.period,
            ),
        );
        students = updateStudentObject(
            students,
            result.student_uid,
            [parseRanResult(studentStatus, result)],
            [parseRanStatus(studentStatus, result)],
        );
    });

    return students;
};

const getLexileStatus = compositeStatus =>
    lexileGradeAvailability.includes(compositeStatus.grade_level) && {
        measure: LEXILE,
        score: compositeStatus.lexile_score,
        compositeScore: compositeStatus.overall_score,
        isComplete: !!compositeStatus.lexile_score,
        schoolYear: compositeStatus.school_year,
        grade: compositeStatus.grade_level,
        period: compositeStatus.period,
    };

export const getD8Result = result => {
    const isMaze = isEqual(result.measure, MAZE);
    return {
        measure: result.measure,
        schoolYear: result.school_year,
        period: result.period,
        grade: result.grade_level,
        formGrade: isImportedResult(result.data_source) ? result.grade_level : result.form_grade,
        measureScores: reformatMeasureScores(result),
        studentId: result.student_uid,
        resultId: result.result_id,
        formUid: isMaze ? result.form_id : result.form_uid,
        formNumber: result.form_number,
        lexileScore: result.lexile_score,
        itemLevelDetails: result.item_level_details,
        date: isMaze ? result.create_date : result[CLIENT_MOD_DATE],
        assessor: result.staff_uid,
        dataSource: result.data_source,
        isSecure: isSecure(result),
        attemptNumber: result.attempt_number,
    };
};

export const getD8Data = (d8Response = {}) => {
    const {
        student_statuses: statuses = [],
        results = [],
        student_growth_zones: growthZones = [],
    } = d8Response;

    let students = {};
    // Parse results for displaying on Benchmark view that don't contribute to composite score (case: student passed LNF or PSF assessment only)
    const resultWithoutStatus = results.filter(result =>
        statuses.every(status => {
            const {
                student_uid: studentUid,
                school_year: year,
                grade_level: grade,
                period,
                measure,
            } = status;
            return !compareResultByStudentMeasureYearGradePeriod(
                result,
                studentUid,
                measure,
                year,
                grade,
                period,
            );
        }),
    );

    if (!isEmpty(resultWithoutStatus)) {
        const bmResults = resultWithoutStatus.filter(result => result.period !== PM);
        students = bmResults.reduce((resultAcc, result) => {
            const parsedResults = [getD8Result(result)];
            const parsedStatuses = [...getStatuses(result, false, buildStatus)];

            return updateStudentObject(
                resultAcc,
                result.student_uid,
                parsedResults,
                parsedStatuses,
            );
        }, {});
    }

    // Parse BM results
    statuses.forEach(status => {
        const studentResults = getStudentResults(status, results);
        const compositeStatus = getCompositeStatus(status, studentResults);
        students = updateStudentObject(
            students,
            status.student_uid,
            studentResults.map(result => getD8Result(result)),
            [
                ...getStatuses(compositeStatus, true, buildStatus).concat(
                    ...studentResults.map(result => getStatuses(result, false, buildStatus)),
                ),
                getLexileStatus(compositeStatus),
            ].filter(elem => elem !== false),
        );
    });
    // Parse PM results
    const pmResults = results.filter(result => result.period === PM);
    pmResults.forEach(pmResult => {
        students = updateStudentObject(
            students,
            pmResult.student_uid,
            [getD8Result(pmResult)],
            getStatuses(pmResult, false, buildStatus),
        );
    });
    // Parse growth zones
    // hiding SUMMER period growthZones
    parseGrowthZones(growthZones)
        .filter(({ period }) => period !== SUMMER)
        .forEach(growthZone => {
            students = updateStudentObject(students, growthZone.studentId, [], [], [growthZone]);
        });
    return students;
};

const parseDecodingMeasureSubscores = ({ measure_subscores: measureSubscores }) =>
    !isEmpty(measureSubscores) &&
    Object.keys(measureSubscores).reduce(
        (result, subscoreKey) => ({
            ...result,
            [subscoreKey.slice(3).toLowerCase()]: {
                score: measureSubscores[subscoreKey].score,
                maxScore: measureSubscores[subscoreKey].max_score,
            },
        }),
        {},
    );

const parseDecodingItemLevelDetails = ({ item_level_details: itemLevelDetails }) =>
    !isEmpty(itemLevelDetails) &&
    itemLevelDetails.responses.reduce(
        (acc, item) => ({
            ...acc,
            [item.item_id]: {
                correct: item.correct,
                responseId: item.response_id,
            },
        }),
        {},
    );

const parseDecodingResult = result => ({
    measure: DO,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    formGrade: result.grade_level,
    studentId: result.student_uid,
    formUid: result.form_id,
    score: result.score,
    maxScore: result.max_score,
    resultId: result.result_id,
    interp: getCommonProficientLevel(result.interpretation, PROFICIENT_LEVEL_MAPPER),
    isComplete: result.is_complete,
    date: result.mod_date,
    measureSubscores: sortSubmeasuresByPeriodAndGrade(
        parseDecodingMeasureSubscores(result),
        result.period,
        result.grade_level,
    ),
    itemLevelDetails: parseDecodingItemLevelDetails(result),
    isSecure: isSecure(result),
});

const parseDecodingStatus = result => ({
    measure: DO,
    schoolYear: result.school_year,
    period: result.period,
    grade: result.grade_level,
    score: result.score,
    resultId: result.result_id,
    interp: getCommonProficientLevel(result.interpretation, PROFICIENT_LEVEL_MAPPER),
    isComplete: result.is_complete,
    date: result.mod_date,
    observations: formatObservations(result.observations),
    isSecure: isSecure(result),
});

export const parseDecodingData = decodingResponse => {
    let students = {};
    if (isEmpty(decodingResponse)) return students;

    decodingResponse.forEach(result => {
        const results = [parseDecodingResult(result)];
        const statuses = [parseDecodingStatus(result)];
        students = updateStudentObject(students, result.student_uid, results, statuses);
    });
    return students;
};

export const parseLecturaData = lecturaResponse => {
    const { student_statuses: statuses, results } = lecturaResponse;

    let students = {};
    // Parse BM results
    statuses.forEach(status => {
        const studentResults = getStudentResults(status, results);
        const compositeStatus = getCompositeStatus(status, studentResults);
        students = sharedUpdateStudentObject(
            students,
            status.student_uid,
            {
                additionalStatuses: getStatuses(compositeStatus, true, buildStatus).concat(
                    ...studentResults.map(result => getStatuses(result, false, buildStatus)),
                ),
            },
            resultStateProperties,
        );
    });
    return students;
};
